"use client";

import { useEffect, useMemo, useState } from "react";
import {
  formatGuideTitle,
  guideBalanceDelta,
  newSidePackage,
  resolveGuidePlan,
  suggestSideGuide,
  type SideGuide,
  type SidePackage,
} from "@/lib/side-guides";
import {
  deleteSideGuide,
  listSideGuides,
  saveSideGuide,
  type PrepSidePlan,
  type SavedDeck,
  type SavedDeckEntry,
} from "@/lib/local-store";
import type { CardRecord } from "@/lib/types";

type Props = {
  deck: SavedDeck;
  cards: CardRecord[];
  opponentLegend: CardRecord | null;
  playOrder: "first" | "second";
  onApply: (plan: PrepSidePlan, meta: { guideId: string; packageId: string | null }) => void;
};

function entryLabel(cardId: string, cardsById: Map<string, CardRecord>) {
  return cardsById.get(cardId)?.name ?? cardId;
}

function QtyList({
  entries,
  cardsById,
  accent,
}: {
  entries: SavedDeckEntry[];
  cardsById: Map<string, CardRecord>;
  accent: "rose" | "emerald";
}) {
  if (entries.length === 0) {
    return <p className="text-[11px] text-gray-600">—</p>;
  }
  const color = accent === "rose" ? "text-rose-300" : "text-emerald-300";
  return (
    <ul className="space-y-0.5">
      {entries.map((e) => (
        <li key={e.cardId} className={`text-xs ${color}`}>
          <span className="font-bold tabular-nums">{e.quantity}×</span>{" "}
          {entryLabel(e.cardId, cardsById)}
        </li>
      ))}
    </ul>
  );
}

function CardPicker({
  pool,
  cardsById,
  onPick,
}: {
  pool: SavedDeckEntry[];
  cardsById: Map<string, CardRecord>;
  onPick: (cardId: string) => void;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return pool.filter((e) => {
      const name = entryLabel(e.cardId, cardsById).toLowerCase();
      return !needle || name.includes(needle);
    });
  }, [pool, cardsById, q]);

  return (
    <div className="mt-1 space-y-1">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Chercher…"
        className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1 text-[11px] outline-none focus:border-emerald-500/40"
      />
      <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto">
        {filtered.slice(0, 40).map((e) => (
          <button
            key={e.cardId}
            type="button"
            onClick={() => onPick(e.cardId)}
            className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] hover:bg-white/10"
            title={`max ${e.quantity}`}
          >
            {entryLabel(e.cardId, cardsById)}
          </button>
        ))}
      </div>
    </div>
  );
}

function bump(entries: SavedDeckEntry[], cardId: string, delta: number, max: number): SavedDeckEntry[] {
  const existing = entries.find((e) => e.cardId === cardId);
  const next = Math.max(0, Math.min(max, (existing?.quantity ?? 0) + delta));
  if (!existing && next <= 0) return entries;
  if (!existing) return [...entries, { cardId, quantity: next }];
  if (next === 0) return entries.filter((e) => e.cardId !== cardId);
  return entries.map((e) => (e.cardId === cardId ? { ...e, quantity: next } : e));
}

export default function SideGuidePanel({
  deck,
  cards,
  opponentLegend,
  playOrder,
  onApply,
}: Props) {
  const cardsById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);
  const [guides, setGuides] = useState<SideGuide[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedPkgId, setSelectedPkgId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SideGuide | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const mainPool = useMemo(
    () =>
      deck.entries
        .map((e) => ({ cardId: e.cardId, quantity: e.quantity, card: cardsById.get(e.cardId) }))
        .filter((e): e is { cardId: string; quantity: number; card: CardRecord } => !!e.card)
        .filter((e) => e.card.type !== "Rune" && e.card.type !== "Battlefield" && e.card.type !== "Legend")
        .map((e) => ({ cardId: e.cardId, quantity: e.quantity })),
    [deck.entries, cardsById],
  );

  const sidePool = useMemo(
    () =>
      (deck.sideboard ?? [])
        .map((e) => ({ cardId: e.cardId, quantity: e.quantity, card: cardsById.get(e.cardId) }))
        .filter((e): e is { cardId: string; quantity: number; card: CardRecord } => !!e.card)
        .map((e) => ({ cardId: e.cardId, quantity: e.quantity })),
    [deck.sideboard, cardsById],
  );

  function refresh() {
    const list = listSideGuides(deck.id);
    setGuides(list);
  }

  useEffect(() => {
    refresh();
    const onSync = () => refresh();
    window.addEventListener("rifthub:storage-sync", onSync);
    return () => window.removeEventListener("rifthub:storage-sync", onSync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.id]);

  // Auto-select guide matching opponent
  useEffect(() => {
    if (!opponentLegend) return;
    const match =
      guides.find((g) => g.againstLegendId === opponentLegend.id) ||
      guides.find((g) =>
        g.againstLabel
          .toLowerCase()
          .includes(opponentLegend.name.replace(/\s*\([^)]*\)\s*$/u, "").trim().toLowerCase()),
      );
    if (match) {
      setActiveId(match.id);
      setDraft(match);
      setSelectedPkgId(match.alternatives[0]?.id ?? null);
      setEditing(false);
    }
  }, [opponentLegend, guides]);

  const active = draft ?? (activeId ? guides.find((g) => g.id === activeId) ?? null : null);

  const preview = useMemo(() => {
    if (!active) return null;
    return resolveGuidePlan(active, selectedPkgId);
  }, [active, selectedPkgId]);

  const balance = preview ? guideBalanceDelta(preview) : null;

  function startNew() {
    const blank: SideGuide = {
      id: "",
      deckId: deck.id,
      againstLegendId: opponentLegend?.id ?? null,
      againstLegendName: opponentLegend?.name ?? "",
      againstLabel: opponentLegend
        ? opponentLegend.name.replace(/\s*\([^)]*\)\s*$/u, "").trim()
        : "",
      playOrder: "both",
      notes: "",
      coreOut: [],
      coreIn: [],
      alternatives: [
        newSidePackage({ label: "A", when: "" }),
        newSidePackage({ label: "B — alternative", when: "ou bien…" }),
      ],
      createdAt: "",
      updatedAt: "",
    };
    setDraft(blank);
    setActiveId(null);
    setSelectedPkgId(blank.alternatives[0]?.id ?? null);
    setEditing(true);
    setHint("Nouveau guide : core + packages A OU B.");
  }

  function applySuggestion() {
    const mainHydrated = mainPool
      .map((e) => ({ card: cardsById.get(e.cardId)!, quantity: e.quantity }))
      .filter((e) => e.card);
    const sbHydrated = sidePool
      .map((e) => ({ card: cardsById.get(e.cardId)!, quantity: e.quantity }))
      .filter((e) => e.card);

    const suggestion = suggestSideGuide({
      opponent: opponentLegend,
      main: mainHydrated,
      sideboard: sbHydrated,
      playOrder,
    });

    const alts: SidePackage[] = suggestion.alternatives.map((a, i) =>
      newSidePackage({
        label: a.label || String.fromCharCode(65 + i),
        when: a.when,
        sideIn: a.sideIn,
        sideOut: a.sideOut,
      }),
    );

    const next: SideGuide = {
      id: draft?.id || activeId || "",
      deckId: deck.id,
      againstLegendId: opponentLegend?.id ?? draft?.againstLegendId ?? null,
      againstLegendName: opponentLegend?.name ?? draft?.againstLegendName ?? "",
      againstLabel:
        draft?.againstLabel ||
        opponentLegend?.name.replace(/\s*\([^)]*\)\s*$/u, "").trim() ||
        suggestion.archetype,
      playOrder: playOrder === "first" || playOrder === "second" ? playOrder : "both",
      notes: suggestion.summary,
      coreOut: suggestion.coreOut,
      coreIn: suggestion.coreIn,
      alternatives: alts.length
        ? alts
        : [newSidePackage({ label: "A" }), newSidePackage({ label: "B" })],
      createdAt: draft?.createdAt ?? "",
      updatedAt: "",
    };
    setDraft(next);
    setSelectedPkgId(next.alternatives[0]?.id ?? null);
    setEditing(true);
    setHint(suggestion.summary);
  }

  function persistDraft() {
    if (!draft) return;
    const saved = saveSideGuide({
      id: draft.id || undefined,
      deckId: deck.id,
      againstLegendId: draft.againstLegendId,
      againstLegendName: draft.againstLegendName,
      againstLabel: draft.againstLabel,
      playOrder: draft.playOrder,
      notes: draft.notes,
      coreOut: draft.coreOut,
      coreIn: draft.coreIn,
      alternatives: draft.alternatives,
    });
    refresh();
    setActiveId(saved.id);
    setDraft(saved);
    setEditing(false);
    setHint("Guide sauvegardé.");
  }

  function removeGuide(id: string) {
    if (!window.confirm("Supprimer ce guide de side ?")) return;
    deleteSideGuide(id);
    refresh();
    if (activeId === id) {
      setActiveId(null);
      setDraft(null);
    }
  }

  function maxFor(cardId: string, pool: SavedDeckEntry[]) {
    return pool.find((e) => e.cardId === cardId)?.quantity ?? 99;
  }

  function updateDraft(patch: Partial<SideGuide>) {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  function updatePkg(pkgId: string, patch: Partial<SidePackage>) {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        alternatives: prev.alternatives.map((p) => (p.id === pkgId ? { ...p, ...patch } : p)),
      };
    });
  }

  return (
    <div className="flex min-h-0 flex-col rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04]">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-white/5 px-3 py-2">
        <p className="text-xs font-bold uppercase tracking-wide text-emerald-300">
          Guides de side
        </p>
        <span className="text-[11px] text-gray-500">Contre X → out/in + A ou B</span>
        <div className="ml-auto flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={applySuggestion}
            className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[11px] font-semibold text-cyan-100 hover:bg-cyan-500/20"
          >
            Suggestion auto
          </button>
          <button
            type="button"
            onClick={startNew}
            className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-semibold hover:bg-white/10"
          >
            Nouveau
          </button>
        </div>
      </div>

      {hint && (
        <p className="border-b border-white/5 px-3 py-1.5 text-[11px] text-cyan-100/80">{hint}</p>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2 sm:p-3">
        {guides.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {guides.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => {
                  setActiveId(g.id);
                  setDraft(g);
                  setSelectedPkgId(g.alternatives[0]?.id ?? null);
                  setEditing(false);
                  setHint(null);
                }}
                className={`rounded-lg px-2 py-1 text-[11px] font-medium ${
                  activeId === g.id
                    ? "bg-emerald-600/40 text-white"
                    : "bg-white/5 text-gray-400 hover:bg-white/10"
                }`}
              >
                {formatGuideTitle(g)}
              </button>
            ))}
          </div>
        )}

        {!active ? (
          <div className="rounded-xl border border-dashed border-white/10 p-4 text-center text-sm text-gray-500">
            Choisis une légende adverse, puis « Suggestion auto » pour générer :
            <br />
            <span className="text-gray-400">
              Contre Darius → out ça · in ça · <strong>ou</strong> package B
            </span>
          </div>
        ) : (
          <>
            {editing ? (
              <div className="space-y-2 rounded-xl border border-white/10 bg-black/25 p-2.5">
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="text-[11px] text-gray-400">
                    Contre (label)
                    <input
                      value={draft?.againstLabel ?? ""}
                      onChange={(e) => updateDraft({ againstLabel: e.target.value })}
                      className="mt-0.5 w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white outline-none focus:border-emerald-500/40"
                      placeholder="Darius / Aggro Fury…"
                    />
                  </label>
                  <label className="text-[11px] text-gray-400">
                    Play order
                    <select
                      value={draft?.playOrder ?? "both"}
                      onChange={(e) =>
                        updateDraft({
                          playOrder: e.target.value as SideGuide["playOrder"],
                        })
                      }
                      className="mt-0.5 w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-sm"
                    >
                      <option value="both">1st & 2nd</option>
                      <option value="first">Go 1st only</option>
                      <option value="second">Go 2nd only</option>
                    </select>
                  </label>
                </div>
                <label className="block text-[11px] text-gray-400">
                  Notes
                  <textarea
                    value={draft?.notes ?? ""}
                    onChange={(e) => updateDraft({ notes: e.target.value })}
                    rows={2}
                    className="mt-0.5 w-full rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-xs outline-none focus:border-emerald-500/40"
                  />
                </label>

                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-rose-300">
                      Core OUT (toujours)
                    </p>
                    <QtyList entries={draft?.coreOut ?? []} cardsById={cardsById} accent="rose" />
                    <CardPicker
                      pool={mainPool}
                      cardsById={cardsById}
                      onPick={(id) =>
                        updateDraft({
                          coreOut: bump(draft?.coreOut ?? [], id, 1, maxFor(id, mainPool)),
                        })
                      }
                    />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(draft?.coreOut ?? []).map((e) => (
                        <button
                          key={e.cardId}
                          type="button"
                          onClick={() =>
                            updateDraft({
                              coreOut: bump(draft?.coreOut ?? [], e.cardId, -1, maxFor(e.cardId, mainPool)),
                            })
                          }
                          className="rounded bg-rose-500/20 px-1.5 py-0.5 text-[10px] text-rose-200"
                        >
                          − {entryLabel(e.cardId, cardsById)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-emerald-300">
                      Core IN (toujours)
                    </p>
                    <QtyList entries={draft?.coreIn ?? []} cardsById={cardsById} accent="emerald" />
                    <CardPicker
                      pool={sidePool}
                      cardsById={cardsById}
                      onPick={(id) =>
                        updateDraft({
                          coreIn: bump(draft?.coreIn ?? [], id, 1, maxFor(id, sidePool)),
                        })
                      }
                    />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(draft?.coreIn ?? []).map((e) => (
                        <button
                          key={e.cardId}
                          type="button"
                          onClick={() =>
                            updateDraft({
                              coreIn: bump(draft?.coreIn ?? [], e.cardId, -1, maxFor(e.cardId, sidePool)),
                            })
                          }
                          className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] text-emerald-200"
                        >
                          − {entryLabel(e.cardId, cardsById)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-200/90">
                      Alternatives (choisir A <span className="text-amber-100">OU</span> B)
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        updateDraft({
                          alternatives: [
                            ...(draft?.alternatives ?? []),
                            newSidePackage({
                              label: String.fromCharCode(65 + (draft?.alternatives.length ?? 0)),
                              when: "autre cas",
                            }),
                          ],
                        })
                      }
                      className="text-[11px] text-amber-200/80 hover:text-amber-100"
                    >
                      + option
                    </button>
                  </div>
                  {(draft?.alternatives ?? []).map((pkg) => (
                    <div
                      key={pkg.id}
                      className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-2"
                    >
                      <div className="mb-1 flex flex-wrap gap-2">
                        <input
                          value={pkg.label}
                          onChange={(e) => updatePkg(pkg.id, { label: e.target.value })}
                          className="min-w-[6rem] flex-1 rounded border border-white/10 bg-black/40 px-2 py-1 text-xs"
                          placeholder="Label"
                        />
                        <input
                          value={pkg.when ?? ""}
                          onChange={(e) => updatePkg(pkg.id, { when: e.target.value })}
                          className="min-w-[8rem] flex-[2] rounded border border-white/10 bg-black/40 px-2 py-1 text-xs"
                          placeholder="quand ? (si board wide…)"
                        />
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <p className="text-[10px] text-rose-300/80">+ OUT</p>
                          <QtyList entries={pkg.sideOut} cardsById={cardsById} accent="rose" />
                          <CardPicker
                            pool={mainPool}
                            cardsById={cardsById}
                            onPick={(id) =>
                              updatePkg(pkg.id, {
                                sideOut: bump(pkg.sideOut, id, 1, maxFor(id, mainPool)),
                              })
                            }
                          />
                        </div>
                        <div>
                          <p className="text-[10px] text-emerald-300/80">+ IN</p>
                          <QtyList entries={pkg.sideIn} cardsById={cardsById} accent="emerald" />
                          <CardPicker
                            pool={sidePool}
                            cardsById={cardsById}
                            onPick={(id) =>
                              updatePkg(pkg.id, {
                                sideIn: bump(pkg.sideIn, id, 1, maxFor(id, sidePool)),
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={persistDraft}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold hover:bg-emerald-500"
                  >
                    Sauver le guide
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      if (activeId) {
                        const g = guides.find((x) => x.id === activeId);
                        if (g) setDraft(g);
                      }
                    }}
                    className="rounded-lg border border-white/10 px-3 py-1.5 text-xs text-gray-400"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-bold text-white">{formatGuideTitle(active)}</h3>
                    {active.notes && (
                      <p className="mt-1 text-[11px] text-gray-400">{active.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      className="rounded-lg border border-white/10 px-2 py-1 text-[11px] hover:bg-white/5"
                    >
                      Éditer
                    </button>
                    {active.id && (
                      <button
                        type="button"
                        onClick={() => removeGuide(active.id)}
                        className="rounded-lg px-2 py-1 text-[11px] text-red-300 hover:bg-red-500/10"
                      >
                        Suppr.
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-2">
                    <p className="mb-1 text-[10px] font-bold uppercase text-rose-300">Core OUT</p>
                    <QtyList entries={active.coreOut} cardsById={cardsById} accent="rose" />
                  </div>
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2">
                    <p className="mb-1 text-[10px] font-bold uppercase text-emerald-300">Core IN</p>
                    <QtyList entries={active.coreIn} cardsById={cardsById} accent="emerald" />
                  </div>
                </div>

                {active.alternatives.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-amber-200/90">
                      Choisir un package (OU)
                    </p>
                    <div className="space-y-1.5">
                      {active.alternatives.map((pkg) => {
                        const selected = selectedPkgId === pkg.id;
                        return (
                          <button
                            key={pkg.id}
                            type="button"
                            onClick={() => setSelectedPkgId(pkg.id)}
                            className={`w-full rounded-xl border p-2.5 text-left transition ${
                              selected
                                ? "border-amber-400/50 bg-amber-500/15 ring-1 ring-amber-400/30"
                                : "border-white/10 bg-black/20 hover:border-white/20"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={`flex h-4 w-4 items-center justify-center rounded-full border text-[9px] ${
                                  selected
                                    ? "border-amber-300 bg-amber-400 text-black"
                                    : "border-white/30"
                                }`}
                              >
                                {selected ? "✓" : ""}
                              </span>
                              <span className="text-sm font-semibold">{pkg.label}</span>
                              {pkg.when ? (
                                <span className="text-[11px] text-gray-500">· {pkg.when}</span>
                              ) : null}
                            </div>
                            <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
                              <QtyList entries={pkg.sideOut} cardsById={cardsById} accent="rose" />
                              <QtyList entries={pkg.sideIn} cardsById={cardsById} accent="emerald" />
                            </div>
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setSelectedPkgId(null)}
                        className={`w-full rounded-lg border px-2 py-1.5 text-left text-[11px] ${
                          selectedPkgId === null
                            ? "border-white/30 bg-white/10 text-white"
                            : "border-white/10 text-gray-500 hover:bg-white/5"
                        }`}
                      >
                        Core seulement (aucun package)
                      </button>
                    </div>
                  </div>
                )}

                {balance && (
                  <p
                    className={`text-[11px] ${
                      balance.delta === 0 ? "text-emerald-300" : "text-amber-200"
                    }`}
                  >
                    Preview : −{balance.out} out / +{balance.in} in
                    {balance.delta !== 0 ? ` · Δ ${balance.delta > 0 ? "+" : ""}${balance.delta} (à équilibrer)` : " · équilibré"}
                  </p>
                )}

                <button
                  type="button"
                  disabled={!preview}
                  onClick={() => {
                    if (!preview || !active) return;
                    onApply(preview, {
                      guideId: active.id,
                      packageId: selectedPkgId,
                    });
                    const pkg = active.alternatives.find((p) => p.id === selectedPkgId);
                    setHint(
                      `Appliqué au plan ${playOrder === "first" ? "Go 1st" : "Go 2nd"}${
                        pkg ? ` · ${pkg.label}` : " · core"
                      }.`,
                    );
                  }}
                  className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-cyan-600 px-3 py-2.5 text-sm font-bold disabled:opacity-40"
                >
                  Appliquer au plan {playOrder === "first" ? "Go 1st" : "Go 2nd"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
